import React, { useEffect, useRef, useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { purchaseOrder, initPurchaseOrder } from '../../model/purchase-order';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api_purchase_order } from '../../api/product';
import CommonTableList from '../common/common-table-list';
import CommonDetail from '../common/common-detail';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Alert, Button, Modal, Tag } from 'antd';
import { isEmpty, convertFormatDateTime } from '../../utils/util';
import PurchaseOrderDetail from './purchase-order-detail';
import PurchaseOrderPrint, { printPurchaseOrderDocument } from './purchase-order-print';
import '../../assets/css/purchase-order.css';
import { describeBlankLines, linesNeedingQuantity } from './po-lines';
import { backendMessage, isConfirmedWrite } from '../../utils/write-result';

/*
 * Phase 7C-6C removed `SEED_PURCHASE_ORDERS`.
 *
 * It was two fabricated purchase orders, complete with an invented
 * operator ("ผู้จัดการ ร้านค้า") and invented dates, and the screen fell
 * back to them whenever the API returned nothing -- and started from
 * them before the first load had even run. A shop comparing the system
 * against its paper records cannot be shown invented purchase orders
 * that look exactly like real ones.
 *
 * The list now starts empty and stays empty when Backend cannot answer;
 * the error itself surfaces through the shared API layer.
 */

const PurchaseOrder: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('');
    const [title, setTitle] = useState('');
    const [data, setData] = useState<purchaseOrder[]>([]);
    const [formData, setFormData] = useState<purchaseOrder>(initPurchaseOrder);
    /**
     * An empty table meant two very different things and looked identical:
     * a shop with no purchase orders, and a Backend that could not be
     * reached. The list must never imply the first when it is the second.
     */
    const [dataState, setDataState] = useState<'loading' | 'ready' | 'error'>('loading');

    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [printData, setPrintData] = useState<purchaseOrder>(initPurchaseOrder);
    const printRef = useRef<HTMLDivElement>(null);

    const onOpenPrintPO = async (record: purchaseOrder) => {
        let fullData = record;
        if (!record.detail || record.detail.length === 0) {
            try {
                const res = await api_purchase_order('GET', {}, record.id);
                if (res && res.id) {
                    fullData = res;
                }
            } catch (e) {
                console.error('Error fetching PO detail for print:', e);
            }
        }
        setPrintData(fullData);
        setIsPrintModalOpen(true);
    };

    const filterField: any = ['document_no', 'vendor_name', 'remark'];
    const placeholder_txt: string = 'ค้นหาตามเลขที่ PO, ชื่อผู้ขายส่ง, หรือหมายเหตุ...';

    const countDraft = data.filter(x => x.status === 1).length;
    const countSent = data.filter(x => x.status === 2).length;
    const countReceived = data.filter(x => x.status === 3).length;

    const columns: ColumnsType<purchaseOrder> = [
        {
            title: 'ลำดับ',
            dataIndex: 'key',
            key: 'key',
            width: '65px',
            align: 'center',
            render: (_text, _record, index) => index + 1
        },
        {
            title: 'เลขที่ใบสั่งซื้อ (PO)',
            dataIndex: 'document_no',
            key: 'document_no',
            width: '150px',
            render: (docNo, record) => (
                <span 
                    style={{ fontWeight: 700, color: 'var(--primary, #FF7A59)', cursor: 'pointer' }}
                    onClick={() => onShowDetail('VIEW', record)}
                >
                    {docNo || '-'}
                </span>
            )
        },
        {
            title: 'วันที่สั่งซื้อ',
            dataIndex: 'create_date',
            key: 'create_date',
            width: '150px',
            render: (date) => typeof date === 'string' ? date : (date ? convertFormatDateTime(date) : '-')
        },
        { 
            title: 'ผู้ขายส่ง (Vendor)', 
            dataIndex: 'vendor_name', 
            key: 'vendor_name',
            render: (text) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
                    <span style={{ fontSize: '15px' }}>🏢</span>
                    <span>{text || 'ไม่ระบุ'}</span>
                </div>
            )
        },
        // Phase 7C-6C rev.2: the `ผู้ทำรายการ` column that used to sit here
        // was removed. Backend has always returned `create_by_name` (LEFT
        // JOIN employee ON e.id = po.create_by) and 7C-6B made `create_by`
        // hold the session-verified employee, so the data is real -- but the
        // approved placement standard keeps the operator in the document
        // header, not in list rows. It is rendered by `CommonDetail` from
        // `recordActor` below. A list/search/filter column can come back if
        // Pilot use proves it earns the width.
        { 
            title: 'จำนวนรวม', 
            dataIndex: 'quantity', 
            key: 'quantity',
            width: '110px',
            align: 'center',
            render: (qty, record) => (
                <div>
                    <span style={{ fontWeight: 700 }}>{qty || 0}</span> ชิ้น
                    <div style={{ fontSize: '11px', color: '#94A3B8' }}>({record.detail?.length || 0} รายการ)</div>
                </div>
            )
        },
        { 
            title: 'ยอดเงินสุทธิ (฿)', 
            dataIndex: 'total_cost', 
            key: 'total_cost',
            width: '140px',
            align: 'right',
            render: (total, record) => (
                <span style={{ fontWeight: 700, color: 'var(--primary, #FF7A59)', fontSize: '14px' }}>
                    {Number(total || record.cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
            )
        },
        { 
            title: 'สถานะ', 
            dataIndex: 'status', 
            key: 'status',
            width: '140px',
            align: 'center',
            render: (status) => {
                if (status === 1) return <Tag color="warning" style={{ borderRadius: '8px', fontWeight: 600 }}>🟡 ฉบับร่าง</Tag>;
                if (status === 2) return <Tag color="processing" style={{ borderRadius: '8px', fontWeight: 600 }}>🔵 ส่งคำสั่งซื้อแล้ว / รอรับสินค้า</Tag>;
                if (status === 3) return <Tag color="success" style={{ borderRadius: '8px', fontWeight: 600 }}>🟢 รับสินค้าครบแล้ว</Tag>;
                return <Tag color="error" style={{ borderRadius: '8px', fontWeight: 600 }}>🔴 ยกเลิก</Tag>;
            }
        },
        { 
            title: 'การดำเนินการ', 
            dataIndex: 'operation', 
            key: 'operation',
            width: '160px', 
            fixed: 'right', 
            render: (_, record) => (
                <div className='iconOperation'>
                    <FontAwesomeIcon icon="eye" title="ดูรายละเอียด" onClick={() => onShowDetail('VIEW', record)} />
                    <FontAwesomeIcon icon="print" title="พิมพ์ใบสั่งซื้อ" onClick={() => onOpenPrintPO(record)} />
                    <FontAwesomeIcon icon="pen-to-square" title="แก้ไขข้อมูล" onClick={() => onShowDetail('EDIT', record)} />
                    <FontAwesomeIcon icon="trash-can" title="ลบข้อมูล" onClick={() => onDelete(record.id)} />
                </div>
            )
        }
    ];

    const init = async () => {
        try {
            const res = await api_purchase_order('LIST', {});
            if (res && Array.isArray(res) && res.length > 0) {
                const formatted = res.map((item: any, idx: number) => ({
                    ...item,
                    key: idx + 1
                }));
                setData(formatted);
            } else {
                setData([]);
            }
            setDataState('ready');
        } catch (e) {
            // Still no fabricated rows -- but say why the table is empty.
            setData([]);
            setDataState('error');
            console.error('Failed to load purchase orders:', e);
        }
    };

    const onShowDetail = (showMode: string, recordData?: purchaseOrder) => {
        if (showMode === 'ADD') {
            setTitle('สร้างใบสั่งซื้อสินค้าใหม่ (New Purchase Order)');
            setFormData({
                ...initPurchaseOrder,
                document_no: '',
                create_date: new Date().toISOString().slice(0, 19).replace('T', ' ')
            });
        } else if (showMode === 'COPY') {
            setTitle('คัดลอกใบสั่งซื้อสินค้า');
            setFormData(recordData ? {
                ...recordData,
                id: 0,
                document_no: '',
                create_date: new Date().toISOString().slice(0, 19).replace('T', ' ')
            } : { ...initPurchaseOrder });
        } else if (showMode === 'EDIT') {
            setTitle('แก้ไขใบสั่งซื้อสินค้า');
            setFormData(recordData ? { ...recordData } : { ...initPurchaseOrder });
        } else if (showMode === 'VIEW') {
            setTitle('รายละเอียดใบสั่งซื้อสินค้า');
            setFormData(recordData ? { ...recordData } : { ...initPurchaseOrder });
        }

        setMode(showMode);
        setOpen(true);
    };

    const onCloseDetail = () => {
        setOpen(false);
    };

    const onSave = async () => {
        let operation = 'INSERT';
        let key_id = undefined;
        const body = { ...formData };

        if (body.id && body.id > 0 && mode === 'EDIT') {
            operation = 'UPDATE';
            key_id = body.id;
        }

        if (body.vendor > 0) {
            if (!body.detail || body.detail.length === 0) {
                Modal.warning({
                    title: 'ไม่มีรายการสินค้า',
                    content: 'กรุณาเพิ่มรายการสินค้าอย่างน้อย 1 รายการในใบสั่งซื้อ',
                    okText: 'ตกลง',
                    centered: true
                });
                return;
            }

            // A line the auto-fill left blank is not an order for zero.
            const blankLines = linesNeedingQuantity(body.detail);
            if (blankLines.length > 0) {
                Modal.warning({
                    title: 'มีสินค้าที่ยังไม่ได้ใส่จำนวน',
                    content: `กรุณาพิมพ์จำนวนสั่งซื้อให้ครบก่อนบันทึก (${blankLines.length} รายการ อยู่บนสุดของตาราง): ${describeBlankLines(blankLines)}`,
                    okText: 'ตกลง',
                    centered: true
                });
                return;
            }

            try {
                const res = await api_purchase_order(operation, body, key_id);
                // `|| res` used to end this test, and the wrapper returns []
                // on any failure -- which is truthy. A write that never
                // happened reported success and vanished on refresh.
                if (isConfirmedWrite(res)) {
                    setOpen(false);
                    Modal.success({
                        title: 'สำเร็จ',
                        content: operation === 'UPDATE' ? 'แก้ไขใบสั่งซื้อเรียบร้อยแล้ว' : 'บันทึกใบสั่งซื้อใหม่เรียบร้อยแล้ว',
                        okText: 'ตกลง',
                        centered: true,
                        onOk() { init(); }
                    });
                } else {
                    showSaveFailed(operation);
                }
            } catch (e) {
                // The 401 has already been handled by the auth interceptor
                // before this point; anything reaching here is a failed save.
                // Backend's own wording beats a generic message when it
                // bothered to explain itself.
                showSaveFailed(operation, e);
            }
        } else {
            Modal.error({
                title: 'ข้อมูลไม่ครบถ้วน',
                content: 'กรุณาระบุเลขที่ใบสั่งซื้อและเลือกผู้ขายส่ง (Vendor) ให้ครบถ้วน',
                okText: 'ตกลง',
                centered: true
            });
        }
    };

    /**
     * Says the save failed and leaves the drawer open with what was typed,
     * so it can be retried without entering it again.
     *
     * This replaces `updateLocalData`, which on a failed write pushed a row
     * into the table with an invented id (`Math.max(...ids) + 1`), closed
     * the drawer and announced success -- a document that existed only on
     * screen until the next refresh.
     */
    const showSaveFailed = (operation: string, error?: any) => {
        const generic = operation === 'UPDATE'
            ? 'ไม่สามารถแก้ไขใบสั่งซื้อได้ ข้อมูลยังไม่ถูกบันทึก กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง'
            : 'ไม่สามารถบันทึกใบสั่งซื้อได้ ข้อมูลยังไม่ถูกบันทึก กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง';
        Modal.error({
            title: 'บันทึกไม่สำเร็จ',
            content: error ? backendMessage(error, generic) : generic,
            okText: 'ตกลง',
            centered: true
        });
    };

    const onDelete = async (key_id: number) => {
        Modal.confirm({
            title: 'ยืนยันการลบใบสั่งซื้อ',
            content: 'คุณต้องการลบใบสั่งซื้อนี้หรือไม่? การดำเนินการนี้ไม่สามารถย้อนกลับได้',
            okText: 'ลบข้อมูล',
            okType: 'danger',
            cancelText: 'ยกเลิก',
            centered: true,
            async onOk() {
                // The result was thrown away and the row removed regardless,
                // so a delete the Backend REFUSED -- it refuses when a
                // confirmed goods receive exists -- still said "ลบเรียบร้อยแล้ว"
                // and the row disappeared until the next refresh.
                const generic = 'ไม่สามารถลบใบสั่งซื้อนี้ได้ และข้อมูลยังคงอยู่ตามเดิม กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง';
                let confirmed = false;
                let reason = generic;
                try {
                    confirmed = isConfirmedWrite(await api_purchase_order('DELETE', {}, key_id));
                } catch (e) {
                    // Backend refuses this delete when a confirmed goods
                    // receive references the document, and says so by name.
                    confirmed = false;
                    reason = backendMessage(e, generic);
                }

                if (!confirmed) {
                    Modal.error({
                        title: 'ลบไม่สำเร็จ',
                        content: reason,
                        okText: 'ตกลง',
                        centered: true
                    });
                    return;
                }

                Modal.success({
                    title: 'สำเร็จ',
                    content: 'ลบใบสั่งซื้อเรียบร้อยแล้ว',
                    okText: 'ตกลง',
                    centered: true,
                    onOk() { init(); }
                });
            }
        });
    };

    const onFormChange = (updatedData: purchaseOrder) => {
        setFormData(updatedData);
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <div className='master-page-container' data-testid="purchase-order-page">
            <div className='po-header-section'>
                <div className='po-header-left'>
                    <div className='po-header-icon'>
                        📋
                    </div>
                    <div>
                        <h2 className='po-header-title'>ใบสั่งซื้อสินค้า (Purchase Order - PO)</h2>
                        <span className='po-header-subtitle'>สร้างและติดตามใบสั่งซื้อสินค้าจากซัพพลายเออร์ พร้อมระบบดึงสินค้าใกล้หมดอัตโนมัติ</span>
                    </div>
                </div>

                <div className='po-header-stats'>
                    <div className='po-stat-card'>
                        <span className='po-stat-label'>ทั้งหมด</span>
                        <span className='po-stat-val'>{data.length} ใบ</span>
                    </div>
                    <div className='po-stat-card'>
                        <span className='po-stat-label'>ฉบับร่าง</span>
                        <span className='po-stat-val' style={{ color: '#D97706' }}>{countDraft}</span>
                    </div>
                    <div className='po-stat-card'>
                        <span className='po-stat-label'>รอรับสินค้า</span>
                        <span className='po-stat-val' style={{ color: '#0284C7' }}>{countSent}</span>
                    </div>
                    <div className='po-stat-card highlight'>
                        <span className='po-stat-label'>รับของแล้ว</span>
                        <span className='po-stat-val highlight'>{countReceived}</span>
                    </div>
                </div>
            </div>

            {dataState === 'error' && (
                <Alert
                    type="error"
                    showIcon
                    data-testid="purchase-order-load-error"
                    style={{ marginBottom: '12px', borderRadius: '8px' }}
                    message="โหลดข้อมูลไม่สำเร็จ"
                    description="ไม่สามารถติดต่อระบบได้ ตารางด้านล่างจึงยังว่างอยู่ ซึ่งไม่ได้แปลว่าไม่มีข้อมูล กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง"
                />
            )}

            <CommonTableList
                page='purchase_order'
                placeholder={placeholder_txt}
                columns={columns}
                data={data}
                filterField={filterField}
                onAdd={() => onShowDetail('ADD')}
            />

            {/* Phase 7C-6E added the Plan B `_iso` twin to this endpoint.
                Prefer it: it states the offset, where `create_date` is a bare
                day-first string this client has to trust is Bangkok. The
                fallback keeps older Backend builds working. Same on the
                Goods Receive and Check Stock screens. */}
            <CommonDetail
                width='960px'
                open={open}
                mode={mode}
                title={title}
                onClosed={onCloseDetail}
                onSaved={onSave}
                recordActor={{ name: formData?.create_by_name, at: (formData as any)?.create_date_iso ?? formData?.create_date }}
                element={
                    <PurchaseOrderDetail
                        purchaseOrder={{ ...formData }}
                        disabled={mode === 'VIEW'}
                        onChange={onFormChange}
                        onReload={init}
                    />
                }
            />

            {/* Purchase Order Print Modal */}
            <Modal
                title={<span><FontAwesomeIcon icon="print" style={{ marginRight: '8px', color: 'var(--primary, #FF7A59)' }} />พิมพ์ใบสั่งซื้อสินค้า</span>}
                open={isPrintModalOpen}
                onCancel={() => setIsPrintModalOpen(false)}
                footer={[
                    <Button key="close" className="btn-cancel" onClick={() => setIsPrintModalOpen(false)}>
                        ปิด
                    </Button>,
                    <Button
                        key="print"
                        type="primary"
                        className="btn-common"
                        onClick={() => printPurchaseOrderDocument(printRef.current)}
                    >
                        <FontAwesomeIcon icon="print" /> พิมพ์เอกสาร
                    </Button>
                ]}
                width={840}
                centered
                destroyOnClose
            >
                <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '8px' }}>
                    <PurchaseOrderPrint ref={printRef} data={printData} />
                </div>
            </Modal>
        </div>
    );
};

export default PurchaseOrder;
